import { createContext, useContext, useEffect, useMemo, useState, type CSSProperties } from "react";
import { detectLocale, translate, type MessageKey } from "./i18n";
import { readableAccentText, validateAccentColor } from "./theme";
import type { AuthLocale, AuthUiProviderProps, ClientBranding } from "./types";

interface AuthUiContextValue extends Omit<AuthUiProviderProps, "children" | "initialLocale"> {
  locale: AuthLocale;
  setLocale: (locale: AuthLocale) => void;
  t: (key: MessageKey, values?: Readonly<Record<string, string>>) => string;
}

const AuthUiContext = createContext<AuthUiContextValue | null>(null);

function storedLocale(): AuthLocale | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const value = window.localStorage.getItem("elefante.auth.locale");
    return value === "pt-BR" || value === "en" ? value : undefined;
  } catch {
    return undefined;
  }
}

export function sanitizeLogoUrl(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value, typeof window === "undefined" ? "https://auth.elefante.dev" : window.location.origin);
    return url.protocol === "https:" ? url.href : undefined;
  } catch {
    return undefined;
  }
}

export function AuthUiProvider({ api, client, children, initialLocale, onNavigate }: AuthUiProviderProps) {
  const [locale, setLocaleState] = useState<AuthLocale>(() => initialLocale ?? storedLocale() ?? detectLocale());
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  const setLocale = (next: AuthLocale) => {
    setLocaleState(next);
    if (typeof window !== "undefined") {
      try { window.localStorage.setItem("elefante.auth.locale", next); } catch { /* Preference remains in memory. */ }
    }
  };
  const navigate = onNavigate ?? ((url: string) => {
    if (typeof window !== "undefined") window.location.assign(url);
  });
  const value = useMemo<AuthUiContextValue>(() => ({
    api,
    client,
    locale,
    setLocale,
    onNavigate: navigate,
    t: (key, values) => translate(locale, key, values)
  }), [api, client, locale, navigate]);
  const accent = validateAccentColor(client.accentColor);
  const style = {
    "--elefante-accent": accent,
    "--elefante-on-accent": readableAccentText(accent)
  } as CSSProperties;
  return <AuthUiContext.Provider value={value}><div className="elefante-auth" style={style}>{children}</div></AuthUiContext.Provider>;
}

export function useAuthUi(): AuthUiContextValue {
  const value = useContext(AuthUiContext);
  if (!value) throw new Error("useAuthUi must be used inside AuthUiProvider");
  return value;
}

export function clientMethods(client: ClientBranding) {
  return new Set(client.enabledMethods ?? ["password", "google", "magic-link", "passkey"]);
}
